import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  generateCodeVerifier,
  generateCodeChallenge,
  decodeLaunchContext,
} from "./FHIRHelper";
import sendServerLogging from "../../utils/sendServerLogging";
import userJSON from "../../utils/userJSON";
import * as Sentry from "@sentry/react";
import { setUserId as setAmplitudeUserId } from "@amplitude/unified";
import jwt_decode from "jwt-decode";
import { markStandaloneUser } from "../../utils/standaloneUser";
import { SESSION_STORAGE_BLOCKED_MESSAGE, setSessionToken } from "../../lib/sessionToken";

// ✅ Module-level flag to prevent duplicate token exchanges across all renders
let tokenExchangeInProgress = false;
let launchRedirectInProgress = false;

export default function FHIRCallback({ setAuth }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState(null);

  const steps = [
    {
      id: "initializing",
      title: "Initializing",
      description: "Setting up VA authorization...",
      icon: "🔄",
    },
    {
      id: "discovering",
      title: "Connecting to VA",
      description: "Discovering VA FHIR endpoints...",
      icon: "🔍",
    },
    {
      id: "redirecting",
      title: "Redirecting to VA",
      description: "Taking you to VA login...",
      icon: "🚀",
    },
    {
      id: "exchanging",
      title: "Exchanging Token",
      description: "Securing your authorization...",
      icon: "🔐",
    },
    {
      id: "authenticating",
      title: "Logging You In",
      description: "Creating your session...",
      icon: "👤",
    },
    {
      id: "complete",
      title: "All Set!",
      description: "Taking you to your dashboard...",
      icon: "✅",
    },
  ];

  const setStep = (stepId) => {
    const stepIndex = steps.findIndex((step) => step.id === stepId);
    if (stepIndex !== -1) setCurrentStep(stepIndex);
  };

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setStep("initializing");

        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const error = searchParams.get("error");
        const iss = searchParams.get("iss");
        const launch = searchParams.get("launch");

        if (error) {
          launchRedirectInProgress = false;
          setError(`VA authorization error: ${error}`);
          return;
        }

        // LAUNCH CONTEXT FLOW
        if (iss && launch && !code) {
          if (launchRedirectInProgress) {
            console.log("⏸️ Launch redirect already in progress, skipping duplicate");
            return;
          }
          launchRedirectInProgress = true;
          setStep("discovering");

          const launchContext = decodeLaunchContext(launch);

          if (launchContext.patient)
            localStorage.setItem("va_patient", launchContext.patient);
          if (launchContext.sta3n)
            localStorage.setItem("va_sta3n", launchContext.sta3n);
          if (launchContext.duz)
            localStorage.setItem("va_duz", launchContext.duz);

          const modifiedLaunchContext = {
            sta3n: launchContext.sta3n,
            duz: launchContext.duz,
          };

          const modifiedLaunch = btoa(JSON.stringify(modifiedLaunchContext));
          const modifiedIss = iss;

          const smartConfigUrl = `${modifiedIss}/.well-known/smart-configuration`;

          // ✅ Use your backend as a proxy to avoid CORS entirely
          const smartConfigResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/auth/va/proxy-smart-config`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                url: smartConfigUrl,
              }),
            },
          );

          if (!smartConfigResponse.ok) {
            throw new Error(
              `Failed to fetch SMART configuration: ${smartConfigResponse.status}`,
            );
          }

          const smartConfig = await smartConfigResponse.json();
          const authorizationEndpoint = smartConfig.authorization_endpoint;
          localStorage.setItem("va_token_endpoint", smartConfig.token_endpoint);

          if (!authorizationEndpoint) {
            throw new Error(
              "No authorization endpoint found in SMART configuration",
            );
          }

          const codeVerifier = generateCodeVerifier();
          localStorage.setItem("va_code_verifier", codeVerifier);
          const codeChallenge = await generateCodeChallenge(codeVerifier);

          const oauthState = "va_" + Math.random().toString(36).substring(2);
          localStorage.setItem("va_oauth_state", oauthState);
          localStorage.setItem("va_fhir_server", modifiedIss);

          const scope =
            "fhirUser offline_access openid profile launch user/DocumentReference.read user/DocumentReference.write user/Binary.read user/Appointment.read user/Condition.read user/MedicationDispense.read user/MedicationRequest.read user/Observation.read user/Patient.read user/Practitioner.read user/Encounter.read user/AllergyIntolerance.read user/PractitionerRole.read";

          const redirectUri = `${window.location.origin}/auth/fhir/callback`;

          const authUrl = new URL(authorizationEndpoint);
          authUrl.searchParams.append(
            "client_id",
            process.env.REACT_APP_VA_CLIENT_ID,
          );
          authUrl.searchParams.append("redirect_uri", redirectUri);
          authUrl.searchParams.append("response_type", "code");
          authUrl.searchParams.append("scope", scope);
          authUrl.searchParams.append("state", oauthState);
          authUrl.searchParams.append("code_challenge", codeChallenge);
          authUrl.searchParams.append("code_challenge_method", "S256");
          authUrl.searchParams.append("launch", modifiedLaunch);

          setStep("redirecting");
          window.location.replace(authUrl.toString());
          return;
        }

        // CODE EXCHANGE FLOW
        if (code && state) {
          launchRedirectInProgress = false;
          // ✅ Check module-level flag FIRST
          if (tokenExchangeInProgress) {
            console.log(
              "⏸️ Token exchange already in progress, skipping duplicate",
            );
            return;
          }

          // ✅ Set flag immediately to block all other attempts
          tokenExchangeInProgress = true;

          setStep("exchanging");

          const storedState = localStorage.getItem("va_oauth_state");
          if (state !== storedState) {
            console.error("OAuth state mismatch");
            window.location.replace("/auth/va/unsupported");
            return;
          }

          const codeVerifier = localStorage.getItem("va_code_verifier");
          if (!codeVerifier) {
            console.error("Missing code verifier");
            window.location.replace("/auth/va/unsupported");
            return;
          }

          const tokenEndpoint = localStorage.getItem("va_token_endpoint");
          const clientId = process.env.REACT_APP_VA_CLIENT_ID;
          const redirectUri = `${window.location.origin}/auth/fhir/callback`;
          const vaFhirServer =
            iss || localStorage.getItem("va_fhir_server") || "";

          try {
            console.log("🔄 Exchanging authorization code for token...");

            const tokenResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/auth/va/token`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  code,
                  code_verifier: codeVerifier,
                  client_id: clientId,
                  redirect_uri: redirectUri,
                  token_url: tokenEndpoint,
                  va_fhir_server: vaFhirServer,
                }),
              },
            );

            const responseData = await tokenResponse.json();

            if (!tokenResponse.ok) {
              console.error("❌ Token exchange failed:", responseData);
              window.location.replace("/auth/va/unsupported");
              return;
            }

            if (
              !responseData.success ||
              !responseData.token ||
              !responseData.userName
            ) {
              console.error("❌ Invalid response from token exchange");
              window.location.replace("/auth/va/unsupported");
              return;
            }

            console.log("✅ Token exchange successful!");
            setStep("authenticating");

            if (!setSessionToken(responseData.token)) {
              setError(SESSION_STORAGE_BLOCKED_MESSAGE);
              return;
            }
            localStorage.setItem("userName", responseData.userName);
            if (responseData.pylonEmailHash) {
              localStorage.setItem("pylonEmailHash", responseData.pylonEmailHash);
            } else {
              localStorage.removeItem("pylonEmailHash");
            }
            markStandaloneUser(false);
            setAuth(true);

            // Extract user_id from JWT token for analytics identification
            const decoded_token = jwt_decode(responseData.token);
            const user_id = decoded_token?.user?.id;
            const user_email = decoded_token?.user?.email || decoded_token?.email;
            if (user_email) {
              localStorage.setItem("userEmail", user_email);
            }

            if (user_id) {
              setAmplitudeUserId(user_id);
              Sentry.setUser({ id: user_id });
            }

            if (user_id) {
              window.pylon = {
                chat_settings: {
                  app_id: "6000e45a-6b53-4401-93b4-a8d408e9b062",
                  ...(user_email ? { email: user_email } : {}),
                  name: responseData.userName || "",
                  ...(responseData.pylonEmailHash
                    ? { email_hash: responseData.pylonEmailHash }
                    : {}),
                },
              };
            }

            setStep("complete");

            sendServerLogging({
              body: {
                ...userJSON(localStorage.getItem("userName")),
                event: "LOGIN",
                eventData: {},
              },
            });

            navigate("/dashboard?refresh=true");
          } catch (err) {
            console.error("❌ Token exchange error:", err);
            window.location.replace("/auth/va/unsupported");
            return;
          }
        }
      } catch (err) {
        console.error("FHIR callback error:", err);
        setError(err.message);
      }
    };

    handleCallback();
  }, [searchParams, setAuth, navigate]);

  const currentStepData = steps[currentStep];

  if (error) {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        className="w-[100dvw] min-h-screen flex items-center justify-center bg-background1 py-12 px-4 focus:outline-none"
      >
        <Helmet><title>Authentication Error – Knowtex</title></Helmet>
        <div className="max-w-lg w-full">
          <div className="text-center bg-white p-16 rounded-xl shadow-knowtex-shadow">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full flex items-center justify-center bg-orange-100">
              <svg
                aria-hidden="true"
                className="w-10 h-10 text-orange-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold mb-3 text-accent1">
              Authorization Issue
            </h1>
            <p role="alert" className="text-base mb-8 text-knowtex-grey">
              {error}
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="w-[100dvw] min-h-screen flex items-center justify-center bg-background1 focus:outline-none"
    >
      <Helmet><title>Signing In – Knowtex</title></Helmet>
      <div className="text-center bg-white p-16 rounded-xl shadow-knowtex-shadow max-w-md">
        <div className="mb-8">
          <div className="flex justify-center mb-6" aria-hidden="true">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index < currentStep
                      ? "bg-ktx-green"
                      : index === currentStep
                        ? "bg-accent2 animate-pulse"
                        : "bg-gray-200"
                  }`}
                />
                {index < steps.length - 1 && (
                  <div
                    className={`w-8 h-0.5 mx-1 transition-all duration-300 ${
                      index < currentStep ? "bg-ktx-green" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="text-sm text-knowtex-grey mb-2">
            Step {currentStep + 1} of {steps.length}
          </div>
        </div>

        <div className="w-16 h-16 mx-auto mb-8 relative" aria-hidden="true">
          <div className="absolute inset-0 border-4 border-[#783BFF] border-t-transparent rounded-full animate-spin"></div>
          <div className="absolute inset-4 flex items-center justify-center text-2xl">
            {currentStepData.icon}
          </div>
        </div>

        <h1 className="sr-only">Signing you in</h1>
        <div role="status" aria-live="polite">
          <p className="text-2xl font-medium text-accent1 mb-3">{currentStepData.title}</p>
          <p className="text-knowtex-grey">{currentStepData.description}</p>

          {currentStep === steps.length - 1 && (
            <div className="mt-6 p-4 bg-ktx-green/10 rounded-lg border border-ktx-green/20">
              <p className="text-sm text-ktx-green font-medium">
                Authorization successful! Redirecting you now...
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
